import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { api } from "./api";
import { OtpScreen } from "./OtpScreen";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function login(mode) {
  window.location.href = mode === "signup" ? "/login?mode=signup" : "/login";
}

export function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    window.location.href = "/";
  };

  return <AuthCtx.Provider value={{ user, setUser, loading, refresh, logout }}>{children}</AuthCtx.Provider>;
}

export function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api
      .post("/auth/session", { session_id: sid })
      .then((r) => {
        setUser(r.data);
        if (r.data.otp_required) return navigate("/dashboard", { replace: true });
        navigate(["owner", "admin"].includes(r.data.role) ? "/admin" : "/dashboard", { replace: true });
      })
      .catch(() => navigate("/", { replace: true }));
  }, [location.hash, navigate, setUser]);

  return <FullLoader label="Signing you in" />;
}

export function FullLoader({ label = "Loading" }) {
  return (
    <div className="min-h-screen grid place-items-center" data-testid="full-loader">
      <div className="flex items-center gap-3 text-muted-foreground">
        <span className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <span className="text-sm">{label}…</span>
      </div>
    </div>
  );
}

export function Protected({ children, owner = false }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/" replace />;
  if (user.otp_required) return <OtpScreen />;
  if (owner && !["owner", "admin"].includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}
